import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

/**
 * Choose which columns a list shows, and reorder them.
 *
 * Reordering is up/down buttons rather than drag-and-drop on purpose: this
 * lives inside a popup menu, where a drag competes with the menu's own scroll
 * and dismissal, and it has to work by keyboard and on a touch screen. Two
 * buttons do the same job in every input mode without a library.
 *
 * `columns` is the ORDERED list, each entry `{ key, label, visible }`.
 */
function ColumnPicker({ columns, onToggle, onMove, onReset, label = 'Columns' }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const visibleCount = columns.filter((c) => c.visible).length;

  return (
    <>
      <Button
        size="small"
        startIcon={<ViewColumnIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {label} ({visibleCount})
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { maxHeight: 420, minWidth: 300 } } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Tick to show. Use the arrows to reorder.
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />

        {columns.map((col, i) => (
          // Not a MenuItem onClick for the whole row: the arrows live inside
          // it, and a click on them must not also toggle the checkbox.
          <MenuItem
            key={col.key}
            disableRipple
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Checkbox
              size="small"
              checked={col.visible}
              onChange={() => onToggle(col.key)}
              inputProps={{ 'aria-label': `Show the ${col.label} column` }}
            />
            <ListItemText
              primary={col.label}
              onClick={() => onToggle(col.key)}
              sx={{ cursor: 'pointer' }}
            />
            <Tooltip title="Move left">
              <span>
                <IconButton
                  size="small"
                  disabled={i === 0}
                  onClick={() => onMove(col.key, -1)}
                  aria-label={`Move ${col.label} left`}
                >
                  <ArrowUpwardIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move right">
              <span>
                <IconButton
                  size="small"
                  disabled={i === columns.length - 1}
                  onClick={() => onMove(col.key, 1)}
                  aria-label={`Move ${col.label} right`}
                >
                  <ArrowDownwardIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
          </MenuItem>
        ))}

        <Divider sx={{ my: 0.5 }} />
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Button size="small" onClick={onReset}>Reset to default</Button>
        </Box>
      </Menu>
    </>
  );
}

export default ColumnPicker;
